import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import constants from '../constants';
import { useTestState } from '../context/TestContext';
import { classifyHearingText, hmacEncode, sanitizePayload } from '../utils';

import { useTranslation } from 'react-i18next';
import greyCheck from '../assets/images/grey-check.png';
import useTracking from '../hooks/useTracking';
import { useLogError } from '../hooks/useLogError';

export const conversionEventsForScore = (
  score: number | null,
): string[] => {
  if (score === 2) return ['JHT_OVERALLSCORE_2', 'JHT_OVERALLSCORE_23'];
  if (score === 3) return ['JHT_OVERALLSCORE_3', 'JHT_OVERALLSCORE_23'];
  return [];
};

const Results = ({
  isB2b2c: _isB2b2c,
  isB2b,
  rht,
}: {
  isB2b2c: boolean;
  isB2b: boolean;
  rht: boolean;
}) => {
  const { state } = useTestState();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { trackEvent } = useTracking();
  const [tracked, setTracked] = useState(false);
  const { logError } = useLogError();

  const payloadSentRef = useRef(false); // Ref to track if payload has been sent
  const markedCompleteRef = useRef(false); // To track "mark complete" payload

  const { t, i18n } = useTranslation('results');

  useEffect(() => {
    if (tracked || state.overallscore == null) {
      return;
    }
    trackEvent('JHT_RESULTS_PAGE_LOAD');
    conversionEventsForScore(state.overallscore).forEach(trackEvent);
    setTracked(true);
  }, [tracked, trackEvent, state.overallscore]);

  useEffect(() => {
    // RHT only
    const handleMarkComplete = async () => {
      const contactData = { id: state.contactid };
      const patientData = { id: state.patientid };
      const activityFacilityData = { id: state.facilityid };
      const hearingResultData = {
        ...state.hearingresultdata,
        id: state.hearingresultid,
        jht_web_complete__c: new Date(),
      };

      const data = {
        contact: contactData,
        hearing_result__c: hearingResultData,
        patient__c: patientData,
        activitytypeid: 'RHT Web Fully Complete',
        activityfacility: activityFacilityData,
      };

      const sanitizedData = sanitizePayload(data);
      if (!sanitizedData) {
        return;
      }

      const activityData = JSON.stringify(sanitizedData);
      const signature = await hmacEncode(activityData, constants['hmacSecret']);

      try {
        const response = await fetch(constants['apiBaseUrl'], {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-AUD-Signature': signature,
          },
          body: activityData,
        });

        if (!response.ok) {
          throw new Error(
            `Failed to POST to ${constants['apiBaseUrl']} on ${data.activitytypeid} for contact id: ${contactData.id}`,
          );
        }
      } catch (error) {
        logError(error);
      }
    };

    // Send the payload upon page load
    const sendViewResultsPayload = async () => {
      const hearingResultData = {
        ...state.hearingresultdata,
        id: state.hearingresultid,
        results_url__c: state.resultsurl,
        overall_score__c: state.overallscore,
        jht_vpta_left__c: state.vptaleft,
        jht_vpta_right__c: state.vptaright,
        end_of_test_notes__c: state.endoftestnotes
      };

      const contactData = {
        id: state.contactid,
        latest_oht_results_url__c: state.resultsurl,
        email_tht_results_klaviyo__c: new Date(),
      };

      const patientData = { id: state.patientid };
      const data = {
        contact: contactData,
        patient__c: patientData,
        hearing_result__c: hearingResultData,
        activitytypeid: rht
          ? 'RHT Web Fully Complete'
          : isB2b
            ? 'JHT Web B2B Fully Complete'
            : 'JHT Web Fully Complete', // JHT Web Fully Complete
      };

      const sanitizedData = sanitizePayload(data);
      if (!sanitizedData) {
        return;
      }

      const activityData = JSON.stringify(sanitizedData);
      const signature = await hmacEncode(activityData, constants['hmacSecret']);

      try {
        const response = await fetch(constants['apiBaseUrl'], {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-AUD-Signature': signature,
          },
          body: activityData,
        });

        if (!response.ok) {
          throw new Error(
            `Failed to POST to ${constants['apiBaseUrl']} on ${data.activitytypeid} for contact id: ${contactData.id}`,
          );
        }
      } catch (error) {
        logError(error);
      }
    };

    // Mark as complete only once (RHT only)
    if (rht && !markedCompleteRef.current) {
      markedCompleteRef.current = true;
      handleMarkComplete();
    }

    // Ensure payload is sent only once
    if (!payloadSentRef.current) {
      payloadSentRef.current = true; // Set ref to true to prevent duplicate sends
      sendViewResultsPayload();
    }
  }, [
    isB2b,
    logError,
    rht,
    state.contactid,
    state.facilityid,
    state.hearingresultid,
    state.overallscore,
    state.patientid,
    state.resultsurl,
    state.vptaleft,
    state.vptaright,
    state.endoftestnotes
  ]);

  const handleViewResults = () => {
    setLoading(true);

    setTimeout(() => {
      // Prefer the shareable results_url (includes scores for cold opens).
      // privacy-bootstrap strips PII before analytics tags; sessionStorage hydrates the page.
      try {
        const fullUrl = new URL(state.resultsurl!);
        const pathAndQuery = fullUrl.pathname + fullUrl.search + fullUrl.hash;
        window.location.replace(window.location.origin + pathAndQuery);
      } catch {
        navigate('/results/detailedresults');
      }
    }, 3000);
  };

  return (
    <div className="results">
      <div className="top-title-text-container">
        <h1 className="top-title-text">{isB2b ? '' : t('thank-you')}</h1>
        <p style={{ textAlign: 'center' }}>{isB2b ? '' : t('results-email')}</p>
      </div>
      <div className="main-content">
        <p style={{ textAlign: 'center' }}>
          {t('hearing-difficulty')}
          <strong>
            {' '}
            {classifyHearingText(state.vptaoverall!, i18n.language)}
          </strong>
        </p>
        <button onClick={handleViewResults} className="link-button">
          <p style={{ color: '#FFF', margin: 0 }}>
            {loading ? t('calculating') : t('see-results')}
          </p>
        </button>
      </div>
      {isB2b ? (
        <></>
      ) : (
        <div className="test-info">
          <img src={greyCheck} alt={t('grey-check-alt')} />
          <p>{t('test-history')}</p>
        </div>
      )}
    </div>
  );
};

export default Results;
